import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ConferenceDay, Session, Speaker } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { CalendarDays, Clock } from 'lucide-react'

import {
    dayById,
    getSession,
    getSpeaker,
    sessions
} from '@/constants/metric'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Tag } from '@/components/generals/tag'
import { BackLink } from '@/components/generals/back-link'
import { Pager } from '@/components/generals/pager'

type SessionPageProps = {
    session: Session
    day: ConferenceDay
    people: Speaker[]
    previous: { slug: string, title: string, time: string }
    next: { slug: string, title: string, time: string }
}

const SessionPage: FC<SessionPageProps> = ({ session, day, people, previous, next }): ReactNode => {
    return (
        <Layout>
            <Seo
                title={session.title}
                description={`${session.blurb} ${day.label}, ${day.date} at ${session.time}. ${session.minutes} minute ${session.kind.toLowerCase()} at Metric Berlin 2026.`}
                ogImage={session.image}
            />

            <article>
                <div className='px-0 tab:px-[60px]'>
                    <div className='grid items-stretch desk:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]'>
                        <div className='fw-container flex flex-col gap-8 py-8 desk:mx-0 desk:max-w-none desk:pr-12'>
                            <div data-load='up' data-load-delay='0' data-load-duration='1.1'>
                                <BackLink href={`/#${day.id}`}>
                                    back to schedule
                                </BackLink>
                            </div>

                            <div data-load='up' data-load-delay='0.08' data-load-duration='1.1' className='flex flex-col gap-4'>
                                <div>
                                    <Tag>
                                        {session.kind}
                                    </Tag>
                                </div>

                                <h1 className='font-sans text-[43px] font-normal leading-[1em] tracking-[-0.055em] text-ink tab:text-[48px] desk:text-[54px]'>
                                    {session.title}
                                </h1>

                                <p className='font-body text-[17px] leading-[1.4em] text-ink-soft'>
                                    {session.blurb}
                                </p>
                            </div>

                            <div data-load='up' data-load-delay='0.16' data-load-duration='1.1' className='flex flex-col gap-4 border-t border-line pt-8'>
                                {session.body.map((paragraph) => (
                                    <p key={paragraph} className='max-w-[54ch] font-body text-[17px] leading-[1.45em] text-ink-soft'>
                                        {paragraph}
                                    </p>
                                ))}
                            </div>

                            <div data-load='up' data-load-delay='0.24' data-load-duration='1.1' className='flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-line pt-8'>
                                <p className='flex items-center gap-2 font-body text-[15px] leading-[1.4em] text-ink-soft'>
                                    <CalendarDays aria-hidden='true' className='size-4 text-ink-mute' />

                                    <span className='text-ink'>
                                        {day.label}
                                    </span>

                                    {day.date}, 2026
                                </p>

                                <p className='flex items-center gap-2 font-body text-[15px] leading-[1.4em] text-ink-soft'>
                                    <Clock aria-hidden='true' className='size-4 text-ink-mute' />

                                    <span className='text-ink'>
                                        {session.time}
                                    </span>

                                    {session.minutes} min
                                </p>
                            </div>
                        </div>

                        <div
                            data-load='right'
                            data-load-delay='0.15'
                            className='relative min-h-[320px] overflow-hidden desk:min-h-full'
                        >
                            <Image
                                src={session.image}
                                alt=''
                                fill
                                priority
                                sizes='(max-width: 1360px) 100vw, 640px'
                                className='object-cover saturate-[0.9]'
                            />

                            <span aria-hidden='true' className='fw-noise absolute inset-0 opacity-[0.08]' />
                        </div>
                    </div>
                </div>

                <Pager
                    previous={{ href: `/sessions/${previous.slug}`, label: previous.title, meta: previous.time }}
                    next={{ href: `/sessions/${next.slug}`, label: next.title, meta: next.time }}
                />

                <section aria-labelledby='session-speakers' className='px-0 py-10 tab:px-[60px] tab:py-14'>
                    <div className='fw-container flex flex-col gap-5'>
                        <h2
                            id='session-speakers'
                            data-reveal='up'
                            className='font-sans text-[22px] font-medium leading-[1.2em] text-ink'
                        >
                            Speakers
                        </h2>

                        <ul className='flex flex-wrap gap-3'>
                            {people.map((person, index) => (
                                <li key={person.slug} data-reveal='up' data-reveal-delay={0.06 * index}>
                                    <Link
                                        href={`/speakers/${person.slug}`}
                                        className='group flex items-center gap-3 rounded-[9px] border border-line bg-[#0d0d0d] p-3 pr-5 transition-colors duration-500 hover:border-[#454545]'
                                    >
                                        <span className='relative block h-[44px] w-[44px] shrink-0 overflow-hidden rounded-[9px]'>
                                            <Image
                                                src={person.image}
                                                alt=''
                                                fill
                                                sizes='44px'
                                                className='object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.08]'
                                            />
                                        </span>

                                        <span className='flex flex-col gap-0.5'>
                                            <span className='font-sans text-[16px] font-medium leading-[1.3em] text-ink'>
                                                {person.name}
                                            </span>

                                            <span className='font-body text-[14px] leading-[1.4em] text-ink-mute'>
                                                {person.role} · {person.company}
                                            </span>
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            </article>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: sessions.map((session) => ({ params: { slug: session.slug } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<SessionPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const session = getSession(slug)
    const day = session ? dayById(session.dayId) : undefined

    if (!session || !day) return { notFound: true }

    const index = sessions.findIndex((item) => item.slug === slug)
    const previous = sessions[(index - 1 + sessions.length) % sessions.length]
    const next = sessions[(index + 1) % sessions.length]

    const people = session.speakers
        .map((speakerSlug) => getSpeaker(speakerSlug))
        .filter((item): item is Speaker => Boolean(item))

    return {
        props: {
            session,
            day,
            people,
            previous: { slug: previous.slug, title: previous.title, time: previous.time },
            next: { slug: next.slug, title: next.title, time: next.time }
        }
    }
}

export default SessionPage
